import DateFormatEnum from "constants/DateFormatEnum";
import { Location } from "history";
import _ from "lodash";
import moment from "moment";
import { match as RouterMatch } from "react-router";
import AgentiProvvModel from "stores/lookup/models/AgentiProvvModel";
import LookUpModel from "stores/lookup/models/LookUpModel";
import BaseHeaderOptionModel from "stores/shared/models/BaseHeaderOptionModel";
import DropdownOptionModel from "stores/shared/models/DropdownOptionModel";
import { number } from "yup";

export type Writeable<T> = { -readonly [P in keyof T]: Writeable<T[P]> };

export type ClonedObject<ModelObject, PropContent> = {
    [K in keyof ModelObject]: ModelObject[K] extends any[]
        ? ModelObject[K][number] extends object
            ? ClonedObject<ModelObject[K][number], PropContent>[]
            : PropContent
        : ModelObject[K] extends object
        ? ClonedObject<ModelObject[K], PropContent>
        : PropContent;
};

export default class FormatUtility {
    private constructor() {}

    public static updateNestedPropertyValue = <T extends object>(
        viewModel: T,
        name: string,
        value: string | number | boolean | Date | any
    ): T => {
        let castedValue = FormatUtility.castValue(value, _.get(viewModel, name));
        _.set(viewModel, name, castedValue);
        return viewModel;
    };

    public static castValue = (
        value: string | number | boolean | Date,
        prop: string | number | boolean | Date
    ): string | number | boolean | Date => {
        if (!value) {
            value = "";
        }
        if (value?.constructor?.name !== prop?.constructor?.name) {
            switch (prop?.constructor?.name) {
                case "String":
                    value = value.toString();
                    break;
                case "Number":
                    value = parseFloat(value.toString());
                    if (isNaN(value)) {
                        value = 0;
                    }
                    break;
                case "Boolean":
                    try {
                        value = Boolean(JSON.parse(value.toString()));
                    } catch (ex) {
                        value = false;
                    }
                    break;
                case "Date":
                    value = moment(value.toString()).toDate();
                    if (isNaN(value.getTime())) {
                        value = new Date();
                    }
                    break;
            }
        }
        return value;
    };

    public static capitalizeFirstLetter = (text: string) => {
        if (text) {
            return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
        }
        return "";
    };

    public static formatMobileNumber = (text: string) => {
        if (text) {
            if (text.includes("/")) text = text.replace("/", "");
            if (text.includes("-")) text = text.replace("-", "");
            if (!text.includes("+")) text = "+39" + text;
            return text;
        }
        return "";
    };

    public static monthsDuration = (months: number | string) => {
        months = parseInt(months.toString());
        return months.toString() + " mesi";
    };

    public static percentage = (percentage: number | string, fractionDigits?: number) => {
        percentage = parseFloat(percentage.toString());
        return (
            percentage.toLocaleString(moment.locale(), {
                minimumFractionDigits: fractionDigits,
                maximumFractionDigits: fractionDigits,
            }) + " %"
        );
    };

    public static currency = (amount: number | string) => {
        amount = parseFloat(amount.toString());
        // console.log(amount.toFixed(2));
        // console.log(amount.toFixed(2).replace(".", ","));
        // console.log(amount.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, "."));
        return (
            "€ " +
            amount
                .toFixed(2)
                .replace(".", ",")
                .replace(/\B(?=(\d{3})+(?!\d))/g, ".")
        );
    };

    public static numberWithSeparators = (input: number | string, step?: number | string, decims?: number): string => {
        if (typeof input === "number") {
            input = input.toString().replace(".", ",");
        }
        if ((input.match(/[.]/g) || []).length === 1 && (input.match(/[,]/g) || []).length === 0) {
            input = input.replace(".", ",");
        }
        let result = input
            .toString()
            .replace(/[^0-9,]/g, "")
            .replace(",", ".");
        if (result === "") {
            return "";
        }
        let decimals = 0;
        if (step) {
            let dotPos = step.toString().indexOf(".");
            if (dotPos > -1) {
                decimals = step.toString().substring(dotPos).length - 1;
                let resultDotPos = result.indexOf(".");
                if (resultDotPos > -1 && result.length > resultDotPos + decimals + 1) {
                    result = result.substring(0, resultDotPos + decimals + 1);
                }
            }
        }

        if (decims) {
            decimals = decims;
        }
        let number = parseFloat(result);
        if (isNaN(number)) {
            number = 0;
        }

        result = number.toFixed(decimals).replace(".", ",");
        if (decimals <= 2) result = result.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

        return result;
    };

    public static numberFromNumberWithSeparators = (input: string): number | undefined => {
        if (input) {
            let number = parseFloat(
                input
                    .toString()
                    .replace(/[^0-9,]/g, "")
                    .replace(",", ".")
            );
            if (isNaN(number) === false) {
                return number;
            }
        }
        return undefined;
    };

    public static dateOrUndefined = (
        date: Date | moment.Moment | string,
        dateFormat: string = DateFormatEnum.ISO_DATE
    ): Date | undefined => {
        if (date) {
            let dateFormatted = date;
            if (date instanceof Date || moment.isMoment(date)) {
                dateFormatted = moment(date).format(dateFormat);
            } else {
                if (date.indexOf("_") > -1) {
                    return undefined;
                }
                let yearLength = 0;
                switch (date.length) {
                    case 10:
                    case 19:
                        yearLength = 4;
                        break;
                    case 8:
                        yearLength = 2;
                        break;
                    default:
                        return undefined;
                }
                if (date.indexOf("-") === yearLength && date.lastIndexOf("-") === yearLength + 3) {
                    dateFormat = "Y".repeat(yearLength) + "-MM-DD HH:mm:ss";
                } else if (date.indexOf("/") === 2 && date.lastIndexOf("/") === 5) {
                    dateFormat = "DD/MM/" + "Y".repeat(yearLength) + " HH:mm:ss";
                } else {
                    return undefined;
                }
                dateFormatted = date;
            }
            let result = moment(dateFormatted, dateFormat);
            if (result.isValid()) {
                return result.toDate();
            } else {
                return undefined;
            }
        } else {
            return undefined;
        }
    };

    public static dateInGivenFormat = (
        input: Date | string,
        formatDest: DateFormatEnum = DateFormatEnum.ISO_DATE
    ): string => {
        let date = FormatUtility.dateOrUndefined(input, DateFormatEnum.ISO_DATE_TIME_SECONDS);
        if (date != null) {
            return moment(date).format(formatDest);
        }
        return "";
    };

    public static differenceMonthsYearsTextual = (
        start: Date | string,
        end: Date | string,
        addMonths?: string
    ): string => {
        let date1 = FormatUtility.dateOrUndefined(start, DateFormatEnum.ITA_DATE);
        let date2 = FormatUtility.dateOrUndefined(end, DateFormatEnum.ITA_DATE);
        if (date1 != null && date2 != null) {
            const startDate = moment(date1).startOf("day");
            const current = moment(date2).startOf("day");
            let years = current.add(addMonths ? addMonths : "0", "months").diff(startDate, "years");
            let months = current.add(addMonths ? addMonths : "0", "months").diff(startDate, "months");
            //console.log(years + ' anni e ' + months % 12 + ' mesi');
            return years + " anni e " + (months % 12) + " mesi";
        }
        return "";
    };

    public static timeInGivenFormat = (
        input: Date | string,
        formatTime: DateFormatEnum = DateFormatEnum.ISO_TIME
    ): string => {
        if (input !== null || input !== undefined) {
            return moment(input).format(formatTime);
        }
        return "";
    };

    public static dateInCommonFormat = (input: Date | string): string => {
        let result = "";
        let date = moment(FormatUtility.dateOrUndefined(input, DateFormatEnum.ISO_DATE_TIME_SECONDS));
        if (date.isSame(moment(), "day")) {
            result = "Oggi";
        } else if (date.isSame(moment().subtract(1, "day"), "day")) {
            result = "Ieri";
        } else if (date.isSame(moment(), "week")) {
            result = FormatUtility.capitalizeFirstLetter(date.format("dddd"));
        } else if (date.isSame(moment().subtract(1, "week"), "week")) {
            result = "La settimana scorsa";
        } else if (date.isSame(moment().subtract(2, "week"), "week")) {
            result = "Due settimane fa";
        } else if (date.isSame(moment().subtract(3, "week"), "week")) {
            result = "Tre settimane fa";
        } else if (date.isSame(moment().subtract(4, "week"), "week")) {
            result = "Quattro settimane fa";
        } else if (date.isSame(moment().subtract(1, "month"), "month")) {
            result = "Un mese fa";
        } else if (date.isSame(moment().subtract(2, "month"), "month")) {
            result = "Quattro settimane fa";
        } else if (date.isSame(moment().subtract(3, "month"), "month")) {
            result = "Tre mesi fa";
        } else if (date.isSame(moment().subtract(4, "month"), "month")) {
            result = "Quattro mesi fa";
        } else {
            result = "Meno recente";
        }
        return result;
    };

    public static dateOrTimeIfToday = (input: Date | string): string => {
        let result = "";
        let date = FormatUtility.dateOrUndefined(input, DateFormatEnum.ISO_DATE_TIME);
        if (date != null) {
            if (moment(date).isSame(moment(), "day")) {
                result = FormatUtility.dateInGivenFormat(date, DateFormatEnum.ITA_TIME);
            } else {
                result = FormatUtility.dateInGivenFormat(date, DateFormatEnum.ITA_DATE);
            }
        }
        return result;
    };

    public static getOmocodiaCodiceFiscale = (codiceFiscale: string, omocodia: number = 0): string => {
        const omocodiaChars = "LMNPQRSTUV";
        const chNumericiPerOmocodia = [14, 13, 12, 10, 9, 7, 6];
        if (omocodia > 0) {
            const chPos = chNumericiPerOmocodia[omocodia - 1];
            let codiceFiscaleSostitutivo = "";
            codiceFiscaleSostitutivo += codiceFiscale.substring(0, chPos);
            const numero = parseInt(codiceFiscale.substring(chPos, chPos + 1));
            if (isNaN(numero) === false) {
                codiceFiscaleSostitutivo += omocodiaChars.charAt(numero);
            } else {
                return "";
            }
            codiceFiscaleSostitutivo += codiceFiscale.substring(chPos + 1);
            codiceFiscale = codiceFiscaleSostitutivo;
            omocodia--;
        }
        return codiceFiscale;
    };

    public static getCodiceFiscale = (
        cognome: string,
        nome: string,
        dataNascita: Date,
        sesso: "M" | "F",
        luogoNascita: string
    ): string => {
        const regexConsonanti = /[^BCDFGHJKLMNPQRSTVWXYZ]/gi;
        const regexVocali = /[^AEIOU]/gi;
        const emptyChar = "X";
        const monthsChars = "ABCDEHLMPRST";
        const chDispari = {
            0: 1,
            1: 0,
            2: 5,
            3: 7,
            4: 9,
            5: 13,
            6: 15,
            7: 17,
            8: 19,
            9: 21,
            A: 1,
            B: 0,
            C: 5,
            D: 7,
            E: 9,
            F: 13,
            G: 15,
            H: 17,
            I: 19,
            J: 21,
            K: 2,
            L: 4,
            M: 18,
            N: 20,
            O: 11,
            P: 3,
            Q: 6,
            R: 8,
            S: 12,
            T: 14,
            U: 16,
            V: 10,
            W: 22,
            X: 25,
            Y: 24,
            Z: 23,
        };
        const chPari = {
            0: 0,
            1: 1,
            2: 2,
            3: 3,
            4: 4,
            5: 5,
            6: 6,
            7: 7,
            8: 8,
            9: 9,
            A: 0,
            B: 1,
            C: 2,
            D: 3,
            E: 4,
            F: 5,
            G: 6,
            H: 7,
            I: 8,
            J: 9,
            K: 10,
            L: 11,
            M: 12,
            N: 13,
            O: 14,
            P: 15,
            Q: 16,
            R: 17,
            S: 18,
            T: 19,
            U: 20,
            V: 21,
            W: 22,
            X: 23,
            Y: 24,
            Z: 25,
        };
        const chControllo = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
        let partCognome = "";
        let partNome = "";
        let partAnno = "";
        let partMese = "";
        let partGiorno = "";
        let partCheckCh = "";
        let codiceFiscale = "";

        // #region parameters validation
        cognome = cognome ? cognome.toUpperCase() : "";
        nome = nome ? nome.toUpperCase() : "";
        luogoNascita = luogoNascita ? luogoNascita.toUpperCase() : "";
        // #endregion parameters validation

        // #region cognome
        partCognome = cognome.replace(regexConsonanti, "");
        partCognome += cognome.replace(regexVocali, "");
        partCognome += emptyChar.repeat(3);
        partCognome = partCognome.substr(0, 3);
        // #endregion cognome

        // #region nome
        partNome = nome.replace(regexConsonanti, "");
        // ignora la seconda consonante se il nome contiene più di 3 consonanti
        if (partNome.length > 3) {
            partNome = partNome.substring(0, 1) + partNome.substring(2, 4);
        }
        partNome += nome.replace(regexVocali, "");
        partNome += emptyChar.repeat(3);
        partNome = partNome.substr(0, 3);
        // #endregion nome

        // #region data nascita e sesso
        const data = FormatUtility.dateInGivenFormat(dataNascita, DateFormatEnum.ITA_DATE);
        if (data) {
            const dd = data.substring(0, 2);
            const mm = data.substring(3, 5);
            const yy = data.substring(8, 10);

            switch (sesso) {
                case "M":
                    partGiorno = dd;
                    break;
                case "F":
                    partGiorno = (parseInt(dd) + 40).toString().padStart(2, "0");
                    break;
            }

            partMese = monthsChars.charAt(parseInt(mm) - 1);

            partAnno = yy;
        }
        // #endregion data nascita e sesso

        codiceFiscale = partCognome + partNome + partAnno + partMese + partGiorno + luogoNascita;

        if (codiceFiscale.length !== 15) {
            return "";
        }

        // #region calcola carattere di controllo
        let somma = 0;
        for (let i = 0; i < 15; i++) {
            const char = codiceFiscale.charAt(i);
            if (i % 2) {
                somma += chPari[char];
            } else {
                somma += chDispari[char];
            }
        }
        somma %= 26;
        partCheckCh = chControllo.charAt(somma);
        // #endregion calcola carattere di controllo

        codiceFiscale += partCheckCh;

        if (codiceFiscale.length !== 16) {
            return "";
        }
        console.log("codddd fiscccccccccccccccc ", codiceFiscale);
        return codiceFiscale;
    };

    //#region find object fields
    public static getNestedFieldsValues = <T extends object>(obj: T, fieldName: string, result: any[]) => {
        if (fieldName in obj) {
            result.push(obj[fieldName]);
        }
        Object.keys(obj).forEach((key: string) => {
            if (typeof obj[key] === "object") {
                FormatUtility.getNestedFieldsValues(obj[key], fieldName, result);
            }
        });
    };
    //#endregion find object fields

    //#region sort array
    public static getSortedArray = <T>(array: T[], fieldName: keyof T, direction: "A" | "D" = "A"): T[] => {
        let arraySorted = array.slice();
        arraySorted = arraySorted.sort((a, b) => {
            if (a[fieldName] < b[fieldName]) {
                if (direction === "D") {
                    return 1;
                } else {
                    return -1;
                }
            }
            if (a[fieldName] > b[fieldName]) {
                if (direction === "D") {
                    return -1;
                } else {
                    return 1;
                }
            }
            return 0;
        });
        return arraySorted;
    };
    //#endregion sort array

    // #region clone object functions
    public static cloneObject = <T, O = {}>(object: T, replaceValuesFunction?: (val: T) => O) => {
        return FormatUtility.deepCloneElement<T, O>(object, replaceValuesFunction);
    };

    private static deepCloneElement = <T, O = {}>(
        value: T,
        replaceValuesFunction?: (val: T) => O
    ): ClonedObject<T, O> | unknown => {
        if (typeof value !== "object" || value === null) {
            if (replaceValuesFunction !== undefined) {
                return replaceValuesFunction(value);
            } else {
                return value;
            }
        } else if (Array.isArray(value)) {
            return FormatUtility.deepCloneArray(
                (value as unknown) as any[],
                (replaceValuesFunction as unknown) as (val: any[]) => O
            );
        } else {
            return FormatUtility.deepCloneObject(value, replaceValuesFunction);
        }
    };

    private static deepCloneObject = <T, O>(source: T, replaceValuesFunction?: (val: T) => O): ClonedObject<T, O> => {
        const excludedKeys = ["IS_BASE_MODEL", "sjsId", "sjsOptions"];
        const result = {};
        Object.keys(source)
            .filter((key) => excludedKeys.includes(key) === false)
            .forEach((key) => {
                const value = source[key];
                result[key] = FormatUtility.deepCloneElement<T, O>(value, replaceValuesFunction);
            }, {});
        return result as ClonedObject<T, O>;
    };

    private static deepCloneArray = <T extends any[], O>(
        collection: T,
        replaceValuesFunction?: (val: T) => O
    ): ClonedObject<T, O> => {
        return collection.map((value) => {
            return FormatUtility.deepCloneElement<T, O>(value, replaceValuesFunction);
        }) as ClonedObject<T, O>;
    };
    // #endregion clone object functions

    // #region public utilities
    public static getPath = (pathname: string): string => {
        if (pathname) {
            if (pathname.length > 0) {
                if (pathname[0] !== "/") {
                    pathname = "/" + pathname;
                }
            }
        }
        return pathname;
    };

    public static getSearchPaths = (pathname: string): string[] => {
        let result = [] as string[];
        if (pathname) {
            pathname = FormatUtility.getPath(pathname);
        }
        return result;
    };

    public static getIdParam = (match: RouterMatch<{ id: string }>) => {
        if (match && match.params && match.params.id) {
            return match.params.id;
        }
        return undefined;
    };

    public static getStateParam = <S, T>(location: Location<any>, paramName: keyof S | undefined) => {
        if (location && location.state) {
            if (paramName !== undefined) {
                if (paramName in location.state) {
                    return location.state[paramName] as T;
                }
            } else {
                return location.state as T;
            }
        }
        return undefined;
    };

    public static getStateParams = <S>(location: Location<any>) => {
        return FormatUtility.getStateParam<S, S>(location, undefined);
    };

    public static getQueryParam = (location: Location<any>, param: string) => {
        return new URLSearchParams(location.search).get(param) ?? "";
    };

    public static getOptionValuesFromLookup = (lookup: LookUpModel[]): DropdownOptionModel[] => {
        return lookup.map(
            (el) =>
                ({
                    value: el.key,
                    description: el.value,
                } as DropdownOptionModel)
        );
    };

    public static getOptionValuesFromAgentiProvvLookup = (lookup: AgentiProvvModel[]): DropdownOptionModel[] => {
        return lookup.map(
            (el) =>
                ({
                    value: el.idAgent.toString() + "|" + el.idDipendenteSub.toString(),
                    description: el.surname + " " + el.name /*+ " (" + el.member + ")"*/,
                } as DropdownOptionModel)
        );
    };

    public static getOptionValuesFromLookupBaseHeader = (lookup: BaseHeaderOptionModel[]): BaseHeaderOptionModel[] => {
        return lookup.map(
            (el) =>
                ({
                    name: el.name,
                    href: el.href,
                    info: {
                        customerName: el.info?.customerName,
                        customerSurname: el.info?.customerSurname,
                        fiscalCode: el.info?.fiscalCode,
                        idEstimate: el.info?.idEstimate,
                        idEstimateRequest: el.info?.idEstimateRequest,
                    },
                } as BaseHeaderOptionModel)
        );
    };

    public static getDescriptionsFromOptionValues = (options: string | string[], lookup: LookUpModel[]) => {
        let result = "";
        if (options && lookup.length) {
            let optionsArray = typeof options === "string" ? options.split(",") : options;
            result = optionsArray
                .map((option) => lookup.find((x) => option === x.key)?.value ?? "")
                .map((label) => label)
                .join(", ");
        }
        return result;
    };

    public static getTextTruncated = (text: string, limit: number): string => {
        if (text && text.length > limit) {
            text = text.substring(0, limit) + "...";
        }
        return text;
    };

    public static downloadPDFFromBase64 = (pdf: string, name: string) => {
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            const fileName = `${name}.pdf`;
            const byteCharacters = atob(pdf);

            let byteNumbers = new Array(byteCharacters.length);

            for (var i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "application/pdf" });

            window.navigator.msSaveOrOpenBlob(blob, fileName);
        } else {
            const linkSource = `data:application/pdf;base64,${pdf}`;
            const downloadLink = document.createElement("a");
            const fileName = `${name}.pdf`;

            downloadLink.href = linkSource;
            downloadLink.download = fileName;
            downloadLink.click();
        }
    };

    public static downloadExcelFromBase64 = (excel: string, name: string) => {
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            const fileName = `${name}.xlsx`;
            const byteCharacters = atob(excel);

            let byteNumbers = new Array(byteCharacters.length);

            for (var i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "application/excel" });

            window.navigator.msSaveOrOpenBlob(blob, fileName);
        } else {
            const linkSource = `data:application/excel;base64,${excel}`;
            const downloadLink = document.createElement("a");
            const fileName = `${name}.xlsx`;

            downloadLink.href = linkSource;
            downloadLink.download = fileName;
            downloadLink.click();
        }
    };

    public static validateHhMm = (value: string) => {
        if (!moment(value, "HH:mm", true).isValid()) value = moment(value).format("HH:mm");
        var isValid = /^([0-1]?[0-9]|2[0-4]):([0-5][0-9])(:[0-5][0-9])?$/.test(value);
        return isValid;
    };

    // #endregion public utilities

    public static getEffectiveDate = () => {
        let d = new Date();
        let incMonth = 1;
        if (d.getDate() >= 25) incMonth = 2;
        d = new Date(d.getFullYear(), d.getMonth() + incMonth, 1);
        return FormatUtility.dateInGivenFormat(d, DateFormatEnum.ITA_DATE);
    };

    public static phoneNumber = (phone?: number | string) => {
        let value = phone;
        if (value) {
            value = value.toString().replace("/", "");
            value =
                value.toString().length > 4 ? value.toString().slice(0, 3) + "/" + value.toString().slice(3) : value;
        }
        return value;
    };
    public static controlDomainMail(email: string | undefined): boolean {
        const allowedDomains = ["@bpp", "@gdf", "@aeronautica.difesa"];
        if (!email) return false;
        const emailLower = email.toLowerCase();
        return allowedDomains.some((domain) => emailLower.includes(domain.toLowerCase()));
    }
}
